import { useEffect, useRef } from 'react'
import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom'

import { Paths } from '../../lib/constants/path'
import useAuth from '../../lib/hooks/use-auth'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import { User } from '../../lib/types/models/users/user'
import { Route } from '../../lib/types/routes'
import { flat } from '../../lib/utils/functions/misc.functions'
import allPages from '../../routes'
import MainLayout from '../layouts/main-layout'

const findMatchingRoute = (pathname: string, routes: Route[]) => {
  // Find a route whose path pattern matches the current pathname.
  // This handles dynamic segments like `client/:id` matching `/client/12`.
  return routes.find((r) => {
    if (!r.path) return false
    // Ensure the pattern starts with a leading slash so matchPath works with pathname
    const pattern = r.path.startsWith('/') ? r.path : `/${r.path}`
    return Boolean(matchPath({ path: pattern, end: true }, pathname))
  })
}

const userHasPermission = (pathname: string, routes: Route[], user?: User) => {
  if (!user) return false

  const matched = findMatchingRoute(pathname, routes)

  return matched?.roles?.length ? matched.roles.includes(user.role) : true
}

const userRedirectMapping: Record<RoleEnum, string> = {
  ADMIN: Paths.AUTH_LOGIN_PAGE,
  PCR: Paths.AUTH_PCR_LOGIN_PAGE,
  STAFF: Paths.AUTH_STAFF_LOGIN_PAGE,
  Ol: Paths.AUTH_OL_LOGIN_PAGE,
  CLIENT: Paths.AUTH_CLIENT_LOGIN_PAGE,
  ACCOUNTANT: Paths.AUTH_LOGIN_PAGE,
  SYSTEM_ADMIN: Paths.AUTH_LOGIN_PAGE
}

const loginRedirectRolePriority: RoleEnum[] = [
  RoleEnum.CLIENT,
  RoleEnum.STAFF,
  RoleEnum.Ol,
  RoleEnum.PCR,
  RoleEnum.ACCOUNTANT,
  RoleEnum.SYSTEM_ADMIN,
  RoleEnum.ADMIN
]

const getLoginRedirectPath = (pathname: string, routes: Route[], userRole?: RoleEnum) => {
  if (userRole) return userRedirectMapping[userRole]

  const matched = findMatchingRoute(pathname, routes)
  const matchedRole = loginRedirectRolePriority.find((role) => matched?.roles?.includes(role))

  return userRedirectMapping[matchedRole ?? RoleEnum.ADMIN]
}

const AuthGuard = () => {
  const { user, userRole, logout } = useAuth()
  const { pathname } = useLocation()
  const isLoggingOut = useRef(false)

  const routes = flat(allPages)
  const hasPermission = userHasPermission(pathname, routes, user)
  const redirectPath = getLoginRedirectPath(pathname, routes, userRole)

  useEffect(() => {
    if (!user || hasPermission || isLoggingOut.current) return

    isLoggingOut.current = true
    logout().catch(() => {
      isLoggingOut.current = false
    })
  }, [hasPermission, logout, user])

  return (
    <>
      {hasPermission ? (
        <MainLayout>
          <Outlet />
        </MainLayout>
      ) : (
        <Navigate replace to={redirectPath} state={{ from: pathname }} />
      )}
    </>
  )
}

export default AuthGuard
